/**
 * Dynamic Tree Service
 *
 * Service for fetching tree items from connector APIs.
 * Supports x-ms-dynamic-tree pattern for file/folder selection
 * with tree navigation (SharePoint, OneDrive, Azure Blob, etc.).
 *
 * Uses the same API patterns as connectorDynamicService.ts.
 */

import type { ConnectionReference } from '@/types/connectionReference';
import type { DynamicTreeConfig, DynamicTreePickerInfo, DynamicTreeParameter } from '@/types/operationParameter';
import type { DereferencedSwagger } from '@/parsers/SwaggerParser';
import { buildDynamicInvokeUrl, buildResolvedServiceProviderConnectionPayload } from './serviceProviderRuntime';
import { findOperationInSwagger, buildDynamicInvokeRequest } from './connectorDynamicService';
import { postManagedApiDynamicInvoke } from './dynamicConnectionService';
import { extractServiceProviderName, isServiceProviderConnector, isManagedApiConnector } from '@/utils/connections';

// =============================================================================
// TYPES
// =============================================================================

/**
 * A single item in the tree (file or folder)
 */
export interface TreeItem {
  /** Unique value/identifier for the item */
  value: string;
  /** Display name shown in the picker */
  displayName: string;
  /** Whether this item is a folder (can be expanded) */
  isFolder: boolean;
  /** Full path to the item */
  fullPath?: string;
  /** Media/icon type (e.g., 'folder', 'file', 'document') */
  mediaType?: string;
  /** Whether this item can be selected (some folders may not be selectable) */
  isSelectable?: boolean;
  /** Raw item data from API */
  rawData?: Record<string, unknown>;
}

/**
 * Request parameters for fetching tree items
 */
export interface FetchTreeItemsRequest {
  /** Connection reference for the connector */
  connectionReference: ConnectionReference;
  /** Connector/API ID */
  connectorId: string;
  /** Tree picker info (open or browse operation) */
  pickerInfo: DynamicTreePickerInfo;
  /** Tree settings from config */
  settings?: DynamicTreeConfig['settings'];
  /** Current parameter values (for resolving dependencies) */
  parameterValues: Record<string, unknown>;
  /** Currently selected item (for browse operations) */
  selectedItem?: TreeItem;
  /**
   * Connector swagger spec — required for managed API connections so we can
   * look up the operation path/method when constructing the
   * `request: { method, path, queries }` payload that
   * `/dynamicInvoke` expects.
   */
  connectorSwagger?: DereferencedSwagger;
  /** Azure access token */
  accessToken: string;
  /** MI token audience (from `useMiAudience`). Required only when `isManagedIdentity` is true. */
  miAudience?: string;
  /** Whether the connection authenticates via MI (from `useManagedConnectionInfo`). */
  isManagedIdentity?: boolean;
  /** Live `connectionRuntimeUrl` from the connection ARM resource (from `useManagedConnectionInfo`). */
  connectionRuntimeUrl?: string;
  /**
   * Whether the designer is in draft mode. Threaded down to app-setting
   * resolution so draft vs published env-settings are read (and cached)
   * independently. Defaults to production when omitted.
   */
  isDraftMode?: boolean;
}

/**
 * Response from tree items fetch
 */
export interface FetchTreeItemsResponse {
  /** Fetched items */
  items: TreeItem[];
  /** Error if fetch failed */
  error?: string;
}

/**
 * Raw response from Azure dynamic API
 */
interface DynamicApiResponse {
  body?: unknown;
  statusCode?: number;
  headers?: Record<string, string>;
}

// =============================================================================
// CONSTANTS
// =============================================================================


// Cache for tree items (30 second TTL - shorter than dropdown cache)
const treeItemsCache = new Map<string, { items: TreeItem[]; timestamp: number }>();
const CACHE_TTL_MS = 30 * 1000;

// =============================================================================
// MAIN FETCH FUNCTION
// =============================================================================

/**
 * Fetch tree items from connector API
 *
 * Supports two patterns:
 * 1. API Connections (managed connectors): POST {connectionId}/dynamicInvoke
 * 2. Service Providers: POST /runtime/webhooks/workflow/api/management/operationGroups/{connectorId}/operations/{operationId}/dynamicInvoke
 */
export async function fetchTreeItems(
  request: FetchTreeItemsRequest
): Promise<FetchTreeItemsResponse> {
  const { connectionReference, connectorId, pickerInfo, settings, parameterValues, selectedItem, connectorSwagger, accessToken, miAudience, isManagedIdentity, connectionRuntimeUrl, isDraftMode = false } = request;

  // Validate access token before making API calls
  if (!isValidAccessToken(accessToken)) {
    return { items: [], error: 'Invalid or missing access token' };
  }

  // Resolve parameters for the API call
  const resolvedParams = resolveTreeParameters(pickerInfo, parameterValues, selectedItem);
  if (!resolvedParams) {
    return { items: [], error: 'Missing required parameters for tree fetch' };
  }

  // Check cache first
  const cacheKey = buildCacheKey(connectionReference.connection.id, pickerInfo.operationId, resolvedParams, {
    isManagedIdentity,
    miAudience,
    connectionRuntimeUrl,
  }) + `::draft:${isDraftMode}`;
  const cached = treeItemsCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return { items: cached.items };
  }

  try {
    // Determine connector type and call appropriate API
    const isServiceProvider = isServiceProviderConnector(connectorId);

    let response: DynamicApiResponse;
    if (isServiceProvider) {
      response = await callServiceProviderDynamicApi(
        connectorId,
        pickerInfo.operationId,
        resolvedParams,
        connectionReference,
        accessToken,
        isDraftMode
      );
    } else {
      // Managed API connections (e.g. Office365, SharePoint, OneDrive) use
      // the same `/dynamicInvoke` endpoint as legacy x-ms-dynamic-values:
      // the body is `{ request: { method, path, queries } }` derived from
      // the operation's swagger definition (NOT the dynamicList shape).
      if (isManagedApiConnector(connectorId) && !connectorSwagger) {
        return {
          items: [],
          error: 'Connector swagger not loaded — required to resolve file picker operation path',
        };
      }
      response = await callManagedApiDynamic(
        connectionReference,
        pickerInfo.operationId,
        resolvedParams,
        accessToken,
        connectorSwagger,
        miAudience,
        isManagedIdentity,
        connectionRuntimeUrl
      );
    }

    // Unwrap dynamicInvoke envelope (`{ response: { statusCode, body } }`)
    // when present; service-provider responses come back as `{ body }` directly.
    const actualResponse = (response as { response?: DynamicApiResponse }).response ?? response;

    // Parse response into tree items
    const items = parseResponseToTreeItems(actualResponse, pickerInfo, settings);

    // Cache the result
    treeItemsCache.set(cacheKey, { items, timestamp: Date.now() });

    return { items };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch tree items';
    console.error('Tree items fetch error:', error);
    return { items: [], error: message };
  }
}

// =============================================================================
// API CALL IMPLEMENTATIONS
// =============================================================================

/**
 * Call managed API connection's dynamicInvoke endpoint for a tree-picker
 * operation (e.g. SharePoint OnFilePickerOpen / OnFilePickerBrowse).
 *
 * URL/body routing (MI vs legacy) and the actual POST are handled in
 * {@link postManagedApiDynamicInvoke}; this function only resolves the
 * swagger operation and shapes the `request` envelope.
 */
async function callManagedApiDynamic(
  connectionReference: ConnectionReference,
  operationId: string,
  parameters: Record<string, unknown>,
  accessToken: string,
  connectorSwagger: DereferencedSwagger | undefined,
  miAudience: string | undefined,
  isManagedIdentity: boolean | undefined,
  connectionRuntimeUrl: string | undefined
): Promise<DynamicApiResponse> {
  const operationDetails = findOperationInSwagger(connectorSwagger, operationId);
  if (!operationDetails) {
    throw new Error(`File picker operation '${operationId}' not found in connector swagger`);
  }

  return postManagedApiDynamicInvoke<DynamicApiResponse>({
    connectionReference,
    request: buildDynamicInvokeRequest(operationDetails, parameters),
    accessToken,
    miAudience,
    isManagedIdentity,
    connectionRuntimeUrl,
    label: 'tree',
  });
}

/**
 * Call service provider connector's dynamicInvoke endpoint
 */
async function callServiceProviderDynamicApi(
  connectorId: string,
  operationId: string,
  parameters: Record<string, unknown>,
  connectionReference: ConnectionReference,
  accessToken: string,
  isDraftMode: boolean
): Promise<DynamicApiResponse> {
  // Service provider connectors are routed via the Otto data plane
  // (see buildServiceProviderRuntimeUrl in ./serviceProviderRuntime).
  const providerName = extractServiceProviderName(connectorId);
  const url = buildDynamicInvokeUrl(providerName, operationId);

  // Service-provider connection payload mirrors connections.json structure.
  const connectionPayload = await buildResolvedServiceProviderConnectionPayload(
    connectorId,
    connectionReference,
    accessToken,
    isDraftMode,
  );

  const body = {
    parameters,
    configuration: {
      connection: connectionPayload,
    },
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Service provider dynamic API failed (${response.status}): ${errorText}`);
  }

  return response.json();
}

// =============================================================================
// RESPONSE PARSING
// =============================================================================

/**
 * Parse API response into TreeItem array
 */
function parseResponseToTreeItems(
  response: DynamicApiResponse | null | undefined,
  pickerInfo: DynamicTreePickerInfo,
  settings?: DynamicTreeConfig['settings']
): TreeItem[] {
  // Validate response exists
  if (!response) {
    console.warn('Tree items response is null or undefined');
    return [];
  }

  // Response body contains the data
  let data: unknown = response.body ?? response;

  // Validate data exists before processing
  if (data === null || data === undefined) {
    console.warn('Tree items response body is null or undefined');
    return [];
  }

  // Navigate to items using itemsPath from operation or settings
  const itemsPath = pickerInfo.itemsPath ?? settings?.collectionPath;
  if (itemsPath) {
    data = getValueByPath(data, itemsPath);
  }

  // Must be an array
  if (!Array.isArray(data)) {
    console.warn('Tree items response is not an array:', data);
    return [];
  }

  // Get path configurations with fallbacks to settings
  const valuePath = pickerInfo.itemValuePath ?? settings?.valuePath ?? 'id';
  const titlePath = pickerInfo.itemTitlePath ?? settings?.titlePath ?? 'name';
  const isParentPath = pickerInfo.itemIsParentPath ?? settings?.folderPropertyName ?? 'isFolder';
  const fullTitlePath = pickerInfo.itemFullTitlePath;
  const selectableFilter = pickerInfo.selectableFilter;
  const mediaPath = settings?.mediaPropertyName;

  // Selection settings
  const canSelectFolders = settings?.canSelectParentNodes ?? true;
  const canSelectFiles = settings?.canSelectLeafNodes ?? true;

  const result: TreeItem[] = [];

  for (const item of data) {
    if (!item || typeof item !== 'object') {
      continue;
    }

    const itemObj = item as Record<string, unknown>;
    const value = String(getValueByPath(itemObj, valuePath) ?? '');

    // Skip items with empty values
    if (!value) {
      continue;
    }

    const displayName = String(getValueByPath(itemObj, titlePath) ?? 'Unknown');
    const isFolder = Boolean(getValueByPath(itemObj, isParentPath));

    // Determine if selectable
    let isSelectable = isFolder ? canSelectFolders : canSelectFiles;
    if (selectableFilter) {
      const filterValue = getValueByPath(itemObj, selectableFilter);
      isSelectable = Boolean(filterValue);
    }

    const treeItem: TreeItem = {
      value,
      displayName,
      isFolder,
      isSelectable,
      rawData: itemObj,
    };

    // Add optional properties only if they have values
    if (fullTitlePath) {
      const fullPathValue = getValueByPath(itemObj, fullTitlePath);
      if (fullPathValue !== undefined) {
        treeItem.fullPath = String(fullPathValue);
      }
    }

    if (mediaPath) {
      const mediaValue = getValueByPath(itemObj, mediaPath);
      if (mediaValue !== undefined) {
        treeItem.mediaType = String(mediaValue);
      }
    }

    result.push(treeItem);
  }

  return result;
}

/**
 * Get value from object by dot-notation path
 */
function getValueByPath(obj: unknown, path: string | undefined): unknown {
  if (!path || obj === null || obj === undefined) {
    return obj;
  }

  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

// =============================================================================
// PARAMETER RESOLUTION
// =============================================================================

/**
 * Resolve parameters for tree API call
 *
 * Handles:
 * - parameterReference: Get value from current parameter values
 * - selectedItemValuePath: Get value from selected tree item
 * - value: Static value
 *
 * Note on "required" parameters: matches Logic Apps' designer behavior —
 * `parameterReference`s in a tree-picker config are NOT treated as hard
 * required dependencies. The picker config refers to the underlying swagger
 * operation's parameters; whether each is required is encoded on the swagger
 * operation, not on the tree config. Logic Apps simply omits unset optional
 * refs from the request body, so we do the same. This avoids gating the
 * Browse button on optional fields like Office 365's `mailboxAddress`.
 */
export function resolveTreeParameters(
  pickerInfo: DynamicTreePickerInfo,
  parameterValues: Record<string, unknown>,
  selectedItem?: TreeItem
): Record<string, unknown> | null {
  if (!pickerInfo.parameters) {
    return {};
  }

  const resolved: Record<string, unknown> = {};

  for (const [key, param] of Object.entries(pickerInfo.parameters)) {
    const resolvedValue = resolveTreeParameter(param, parameterValues, selectedItem);

    // Static `value` (including empty string) is always intentional — include
    // it even when the value is "". For example, GitHub's repos-get-content
    // uses `path: { value: "" }` to request the repo root.
    const isStaticValue = param.value !== undefined;

    if (!isStaticValue && (resolvedValue === undefined || resolvedValue === '' || resolvedValue === null)) {
      // Unset optional ref — just omit from the request. The runtime treats
      // missing optional swagger params as not-supplied.
      continue;
    }

    resolved[key] = resolvedValue;
  }

  return resolved;
}

/**
 * Resolve a single tree parameter
 */
function resolveTreeParameter(
  param: DynamicTreeParameter,
  parameterValues: Record<string, unknown>,
  selectedItem?: TreeItem
): unknown {
  // Static value takes precedence
  if (param.value !== undefined) {
    return param.value;
  }

  // Selected item value path (for browse operations)
  if (param.selectedItemValuePath && selectedItem) {
    if (param.selectedItemValuePath === 'value' || param.selectedItemValuePath === 'id') {
      return selectedItem.value;
    }
    if (param.selectedItemValuePath === 'displayName' || param.selectedItemValuePath === 'name') {
      return selectedItem.displayName;
    }
    if (param.selectedItemValuePath === 'fullPath') {
      return selectedItem.fullPath;
    }
    // Try to get from raw data
    if (selectedItem.rawData) {
      return getValueByPath(selectedItem.rawData, param.selectedItemValuePath);
    }
  }

  // Parameter reference
  if (param.parameterReference) {
    return parameterValues[param.parameterReference];
  }

  return undefined;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Validate that an access token exists and has basic JWT structure.
 * This is a lightweight client-side check to fail fast before API calls.
 */
function isValidAccessToken(token: string | null | undefined): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }
  // Basic JWT structure check: three base64 segments separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }
  // Each part should be non-empty and contain only valid base64url characters
  const base64UrlRegex = /^[A-Za-z0-9_-]+$/;
  return parts.every((part) => part.length > 0 && base64UrlRegex.test(part));
}

/**
 * Build cache key for tree items.
 * Uses deterministic hashing to prevent cache poisoning from maliciously
 * crafted parameter values. MI routing fields are included so a
 * re-classification (e.g. once `useManagedConnectionInfo` settles) produces
 * a cache miss instead of returning a stale bare-route response.
 */
function buildCacheKey(
  connectionId: string,
  operationId: string,
  parameters: Record<string, unknown>,
  miRouting: { isManagedIdentity?: boolean | undefined; miAudience?: string | undefined; connectionRuntimeUrl?: string | undefined }
): string {
  const normalizedParams = normalizeForCacheKey(parameters);
  const mi = `${miRouting.isManagedIdentity ?? false}::${miRouting.miAudience ?? ''}::${miRouting.connectionRuntimeUrl ?? ''}`;
  return `tree::${connectionId}::${operationId}::${simpleHash(normalizedParams)}::${mi}`;
}

/**
 * Normalize an object for cache key generation.
 * Sorts keys recursively to ensure deterministic output.
 */
function normalizeForCacheKey(obj: unknown): string {
  if (obj === null || obj === undefined) {
    return 'null';
  }
  if (typeof obj !== 'object') {
    return String(obj);
  }
  if (Array.isArray(obj)) {
    return '[' + obj.map(normalizeForCacheKey).join(',') + ']';
  }
  // Sort keys for deterministic ordering
  const sortedKeys = Object.keys(obj as Record<string, unknown>).sort();
  const pairs = sortedKeys.map((key) => {
    const value = (obj as Record<string, unknown>)[key];
    return `${key}:${normalizeForCacheKey(value)}`;
  });
  return '{' + pairs.join(',') + '}';
}

/**
 * Simple hash function for cache keys.
 * Uses djb2 algorithm - fast and sufficient for cache key deduplication.
 */
function simpleHash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  // Convert to unsigned 32-bit integer and then to hex
  return (hash >>> 0).toString(16);
}

/**
 * Clear the tree items cache
 */
export function clearTreeItemsCache(): void {
  treeItemsCache.clear();
}

/**
 * Clear cache entries for a specific connection
 */
export function clearTreeCacheForConnection(connectionId: string): void {
  for (const key of treeItemsCache.keys()) {
    if (key.includes(connectionId)) {
      treeItemsCache.delete(key);
    }
  }
}

// =============================================================================
// DEPENDENCY UTILITIES
// =============================================================================

/**
 * Get list of parameter names that the tree picker depends on
 */
export function getTreeDependencyParameterNames(config: DynamicTreeConfig): string[] {
  const deps = new Set<string>();

  // Check open operation
  if (config.open.parameters) {
    for (const param of Object.values(config.open.parameters)) {
      if (param.parameterReference) {
        deps.add(param.parameterReference);
      }
    }
  }

  // Check browse operation (may have different dependencies)
  if (config.browse.parameters) {
    for (const param of Object.values(config.browse.parameters)) {
      if (param.parameterReference) {
        deps.add(param.parameterReference);
      }
    }
  }

  return Array.from(deps);
}

/**
 * Check if all required dependencies are satisfied for tree fetch.
 *
 * Mirrors Logic Apps' behavior: only static `value` parameters and
 * `parameterReference`s with available values are needed for the *open* call.
 * Optional unset refs are silently dropped. As long as the picker info
 * exists, the open call is allowed to fire — the runtime will return an
 * error if something genuinely required is missing.
 */
export function areTreeDependenciesSatisfied(
  config: DynamicTreeConfig,
  parameterValues: Record<string, unknown>
): boolean {
  // For initial fetch (open), check if dependencies are met
  const result = resolveTreeParameters(config.open, parameterValues, undefined);
  return result !== null;
}
